/*
Usage Example:
const userContext = useUserContextStore(); // however you get it
const perms = usePermissions(() => userContext.context);

if (perms.isAdmin.value) { }
if (perms.hasOfficeData.value) {  }
*/

import { computed, MaybeRefOrGetter, toValue, type ComputedRef } from "vue";
import type { UserContext, UserRole } from "@/core/store/context";
import { RoleEnum } from "@/core/enum";

type PermissionsMap = Record<string, boolean>;

const adminOnlyPages = new Set(["EmployeeUploadPage"]);
const disabledPages = new Set(["ResumePage"]);

const safePermissions = (ctx: UserContext | null | undefined): PermissionsMap =>
  (ctx?.permissions?.page ?? {}) as PermissionsMap;

export function usePermissions(context: MaybeRefOrGetter<UserContext | null | undefined>) {
  const ctx = computed(() => toValue(context));

  // --- role helpers ---
  const role = computed(() => ctx.value?.role);

  const hasRole = (r: UserRole): ComputedRef<boolean> => computed(() => role.value === r);

  const isManager = hasRole(RoleEnum.MANAGER);
  const isAdmin = hasRole(RoleEnum.ADMINISTRATOR);
  const isEditor = hasRole(RoleEnum.EDITOR);
  const isEmployee = hasRole(RoleEnum.EMPLOYEE);
  const isDeliveryLeader = hasRole(RoleEnum.DELIVERYLEADER);

  // --- permission helpers ---
  const permissions = computed(() => safePermissions(ctx.value));

  const hasPermission = (key: string): ComputedRef<boolean> =>
    computed(() => !!permissions.value[key]);

  const hasAllData = hasPermission("allData");
  const hasManagerData = hasPermission("managerData");
  const hasOfficeData = hasPermission("officeData");
  const hasRegionData = hasPermission("regionalData");
  const hasTemporaryData = hasPermission("temporaryData");

  // --- page helpers ---
  const hasPagePermission = (userRole: RoleEnum, pageRoles: RoleEnum[]) =>
    pageRoles.includes(userRole);

  const isAdminOnlyPage = (page: string) => adminOnlyPages.has(page);
  const isPagePermissionDisabled = (page: string) => disabledPages.has(page);

  const isAuthenticated = computed(() => ctx.value?.authenticated);

  return {
    // raw
    ctx,
    role,
    permissions,

    // roles
    hasRole,
    isManager,
    isAdmin,
    isEditor,
    isEmployee,
    isDeliveryLeader,

    // permissions
    hasPermission,
    hasAllData,
    hasManagerData,
    hasOfficeData,
    hasRegionData,
    hasTemporaryData,

    // pages
    hasPagePermission,
    isAdminOnlyPage,
    isPagePermissionDisabled,

    isAuthenticated,
  };
}
